import React, { useState, useEffect } from "react";
import { useSEO } from "@/lib/seo";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { StatusBadge } from "@/components/StatusBadge";
import { SmsOptInCard } from "@/components/SmsOptInCard";
import {
  ArrowRight, PenLine, MessageSquareText, ShieldCheck, Check, MessageSquare,
  Signal, Wifi, BatteryFull,
  CheckCircle2, Clock, AlertTriangle, Banknote, Ban, HelpCircle, ChevronLeft, ChevronRight,
} from "lucide-react";

const TAB_ICONS = {
  confirm: CheckCircle2, due: Clock, overdue: AlertTriangle, paid: Check,
  expected: Banknote, missed: AlertTriangle, received: Banknote, stop: Ban, help: HelpCircle,
};

const DEMO_ROWS = [
  { type: "invoice", reference_code: "INV-3814", name: "North Coast Studio", amount: "$1,260.00", date: "Sep 4", display_status: "Due Today" },
  { type: "invoice", reference_code: "INV-3815", name: "Brightline Creative", amount: "$875.00", date: "Sep 8", display_status: "Upcoming" },
  { type: "payout", reference_code: "PAY-1402", name: "Freelance Platform", amount: "$640.00", date: "Sep 2", display_status: "Pending" },
  { type: "invoice", reference_code: "INV-3798", name: "Cedar & Fifth", amount: "$1,450.00", date: "Aug 18", display_status: "Overdue", days_overdue: 12 },
  { type: "invoice", reference_code: "INV-3791", name: "Atlas Home Services", amount: "$2,100.00", date: "Aug 18", display_status: "Paid" },
];

const SMS_TABS = [
  { key: "confirm", label: "Confirmation", body: "PaySignal: You're confirmed. We'll text account notifications about activity in your account. Reply STOP to opt out, HELP for help." },
  { key: "due", label: "Invoice due today", body: "PaySignal: INV-3814 for North Coast Studio, $1,260.00, is due today. Reply STOP to opt out." },
  { key: "overdue", label: "Invoice overdue", body: "PaySignal: INV-3798 for Cedar & Fifth, $1,450.00, is now 12 days past due. Reply STOP to opt out, HELP for help." },
  { key: "paid", label: "Invoice paid", body: "PaySignal: You marked INV-3791 ($2,100.00, Atlas Home Services) as paid. 2026 total is now $18,420. Reply STOP to opt out." },
  { key: "expected", label: "Payout expected", body: "PaySignal: PAY-1402 ($640.00, Freelance Platform) is expected today. Reply STOP to opt out." },
  { key: "missed", label: "Payout missed", body: "PaySignal: PAY-1398 ($412.66, Gig Drive Weekly) has passed its expected date and is not marked received. Reply STOP to opt out, HELP for help." },
  { key: "received", label: "Payout received", body: "PaySignal: You marked PAY-1390 ($950.00, Design Marketplace) as received. 2026 total is now $19,370. Reply STOP to opt out." },
  { key: "stop", label: "STOP", body: "PaySignal: You are unsubscribed and will receive no further texts. Reply START to resubscribe." },
  { key: "help", label: "HELP", body: "PaySignal: Account alerts for invoices & payouts. Support: contact@paysignal.ai. Reply STOP to opt out." },
];

const ACTS = [
  { n: "01", Icon: PenLine, title: "Log it, effortlessly", body: "Jot down a client, an amount, and a date — invoice or platform payout. Fifteen seconds and it's in your ledger. No categories to learn, no bookkeeping ritual." },
  { n: "02", Icon: MessageSquareText, title: "Optional, quiet text alerts", body: "If you want a nudge, PaySignal can text you the moment something shifts. Transactional only, never marketing — and you confirm your number first. Silence is the default." },
  { n: "03", Icon: ShieldCheck, title: "Peace of mind, in your timezone", body: "Every record reads itself as Upcoming, Due Today, or Overdue in your local time. You stop doing date math in your head and simply glance to know where the money stands." },
];

function PhoneDemo({ body, messageKey }) {
  return (
    <div className="relative mx-auto w-[312px]">
      <div className="absolute inset-0 -m-6 rounded-[3.8rem] bg-olive-light pointer-events-none" />
      <div className="relative mx-auto w-[300px] rounded-[3.2rem] bg-espresso p-[4px] shadow-[0_40px_90px_-25px_rgba(42,36,33,0.55)]">
        <span className="absolute -left-[3px] top-[120px] w-[3px] h-9 rounded-l bg-espresso/80" />
        <span className="absolute -left-[3px] top-[172px] w-[3px] h-16 rounded-l bg-espresso/80" />
        <span className="absolute -right-[3px] top-[150px] w-[3px] h-24 rounded-r bg-espresso/80" />
        <div className="relative rounded-[2.95rem] bg-cream overflow-hidden ring-1 ring-black/5 flex flex-col min-h-[620px]">
          <div className="absolute top-2.5 left-1/2 -translate-x-1/2 z-20 w-24 h-6 rounded-full bg-espresso" />
          <div className="flex items-center justify-between px-6 pt-3.5 pb-1 text-[11px] font-semibold text-espresso">
            <span className="font-num">9:41</span>
            <span className="flex items-center gap-1">
              <Signal size={13} strokeWidth={2.5} />
              <Wifi size={13} strokeWidth={2.5} />
              <BatteryFull size={17} strokeWidth={2} />
            </span>
          </div>
          <div className="bg-white/80 backdrop-blur px-4 py-3 border-b border-warmborder flex items-center gap-2">
            <span className="relative w-8 h-8 rounded-full bg-olive text-white grid place-items-center text-xs font-bold">P
              <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-olive border-2 border-white" />
            </span>
            <div><p className="text-sm font-semibold leading-none text-espresso">PaySignal</p><p className="text-[10px] text-warmmuted mt-0.5">Account alerts · SMS</p></div>
          </div>
          <div className="flex-1 flex flex-col px-4 pt-5 pb-6 space-y-1.5">
            <p className="text-[10px] text-center text-warmmuted font-medium pb-1">Text Message · Today</p>
            <div key={messageKey} className="animate-fade-up">
              <div className="bg-white border border-warmborder rounded-2xl rounded-tl-md px-3.5 py-2.5 text-[13px] text-espresso leading-relaxed shadow-sm max-w-[85%]">
                {body}
              </div>
              <p className="text-[10px] text-warmmuted pl-1 pt-1">Delivered</p>
            </div>
            <p className="text-[10px] text-center text-warmmuted uppercase tracking-wider font-semibold mt-auto pt-3">Sample message · demo only</p>
          </div>
          <div className="pb-2.5 flex justify-center">
            <span className="w-28 h-1 rounded-full bg-espresso/25" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Landing() {
  const [tab, setTab] = useState(SMS_TABS[1]);
  const HERO_ALERTS = ["due", "overdue", "paid"].map((k) => SMS_TABS.find((t) => t.key === k));
  const [heroIdx, setHeroIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setHeroIdx((i) => (i + 1) % HERO_ALERTS.length), 3400);
    return () => clearInterval(id);
  }, [HERO_ALERTS.length]);

  useSEO({
    title: "PaySignal — Know exactly what money is coming",
    description: "Track invoices and expected platform payouts from one calm ledger. Optional, quiet text alerts when dates or payment states change. Built for freelancers, contractors and gig workers.",
    canonical: window.location.origin + "/",
    type: "website",
  });

  return (
    <div className="min-h-screen bg-cream text-espresso">
      {/* Nav */}
      <SiteHeader />

      {/* Act I — Hero (asymmetrical editorial) */}
      <section className="max-w-6xl mx-auto px-6 pt-16 pb-24 lg:pt-24 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 animate-fade-up">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold text-[#8C6D58]">
            <span className="w-6 h-px bg-[#8C6D58]" /> For freelancers, contractors &amp; gig workers
          </span>
          <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.06] tracking-tight mt-6 text-espresso">
            Money shouldn't require{" "}
            <em className="italic font-normal text-olive">manual vigilance.</em>
          </h1>
          <p className="body-lg text-lg sm:text-xl text-espresso-soft mt-6 leading-relaxed max-w-xl">
            You do the work. You send the invoice. Then begins the quiet, exhausting job of
            <span className="text-espresso"> remembering</span> — who owes you, what's late, when the payout lands.
            PaySignal keeps that watch for you, in one calm ledger.
          </p>
          <div className="flex flex-wrap gap-3 mt-9">
            <Link to="/signup"><Button size="lg" className="rounded-full px-7 h-12" data-testid="hero-start">Start tracking free <ArrowRight size={18} className="ml-1.5" /></Button></Link>
            <Link to="/how-it-works">
              <Button size="lg" variant="outline" className="rounded-full px-7 h-12 border-olive/40 text-olive hover:bg-olive-light hover:text-olive" data-testid="hero-how">
                Explore how it works
              </Button>
            </Link>
          </div>
          <p className="text-sm text-warmmuted mt-5">No card required · Alerts always optional · Cancel anytime</p>
        </div>

        {/* Floating tactile journal card */}
        <div className="lg:col-span-5 animate-fade-up" style={{ animationDelay: "0.12s" }}>
          <div className="relative">
            <div className="absolute -inset-4 rounded-[2rem] bg-olive-light -rotate-2" />
            <div className="relative bg-white border border-warmborder rounded-[1.75rem] shadow-[0_40px_90px_-45px_rgba(42,36,33,0.55)] p-6 rotate-[-1.5deg] transition-transform duration-500 hover:rotate-0">
              <div className="flex items-center justify-between">
                <p className="font-heading text-lg font-semibold text-espresso">Your ledger</p>
                <span className="text-xs font-num text-warmmuted">Sep 2026</span>
              </div>
              <div className="mt-5 space-y-3">
                {DEMO_ROWS.slice(0, 4).map((r) => (
                  <div key={r.reference_code} className="flex items-center justify-between gap-3 border-b border-warmborder/70 last:border-0 pb-3 last:pb-0">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-espresso truncate">{r.name}</p>
                      <p className="text-xs font-num text-warmmuted">{r.reference_code} · {r.date}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-num font-semibold text-espresso">{r.amount}</p>
                      <div className="mt-1 flex justify-end"><StatusBadge status={r.display_status} daysOverdue={r.days_overdue} /></div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-5 flex items-center justify-between rounded-2xl bg-oatmeal px-4 py-3">
                <span className="text-xs uppercase tracking-wider font-semibold text-espresso-soft">Paid year-to-date</span>
                <span className="font-num font-semibold text-espresso">$18,420</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The narrative pull-quote */}
      <section id="story" className="bg-oatmeal border-y border-warmborder">
        <div className="max-w-4xl mx-auto px-6 py-20 lg:py-28 text-center">
          <span className="text-xs uppercase tracking-[0.22em] font-semibold text-[#8C6D58]">The quiet burden</span>
          <blockquote className="font-serif font-light text-2xl sm:text-3xl lg:text-4xl leading-snug text-espresso mt-6">
            "The invoice was the easy part. It's the <em className="italic text-olive">weeks after</em> — the mental
            tabs left open, the calendar you keep in your head — that wear you down."
          </blockquote>
          <p className="body text-base text-espresso-soft mt-8 max-w-xl mx-auto leading-relaxed">
            PaySignal exists to close those tabs. Not another accounting suite — just a place that remembers
            what's owed, tells you the day it matters, and otherwise stays out of your way.
          </p>
        </div>
      </section>

      {/* Act II — Three acts */}
      <section className="max-w-6xl mx-auto px-6 py-20 lg:py-28">
        <div className="max-w-2xl">
          <span className="text-xs uppercase tracking-[0.22em] font-semibold text-[#8C6D58]">How it works</span>
          <h2 className="font-serif font-normal text-3xl sm:text-4xl lg:text-5xl tracking-tight text-espresso mt-4 leading-tight">
            Three unhurried steps to knowing where you stand.
          </h2>
        </div>
        <div className="mt-14 space-y-10">
          {ACTS.map(({ n, Icon, title, body }, i) => (
            <div key={n} data-testid={`act-${i + 1}`}
              className="grid md:grid-cols-12 gap-6 items-start">
              <div className="md:col-span-3 flex items-center gap-4">
                <span className="font-serif text-5xl lg:text-6xl font-light text-olive/30 leading-none">{n}</span>
                <span className="grid place-items-center w-12 h-12 rounded-2xl bg-olive/10 text-olive shrink-0"><Icon size={22} /></span>
              </div>
              <div className="md:col-span-9 md:border-l border-warmborder md:pl-8">
                <h3 className="font-heading text-xl sm:text-2xl font-semibold text-espresso">{title}</h3>
                <p className="body text-base text-espresso-soft mt-3 leading-relaxed max-w-2xl">{body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Ledger showcase */}
      <section id="ledger" className="bg-oatmeal border-y border-warmborder">
        <div className="max-w-6xl mx-auto px-6 py-20 lg:py-28">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <span className="text-xs uppercase tracking-[0.22em] font-semibold text-[#8C6D58]">A living page</span>
              <h2 className="font-serif font-normal text-3xl sm:text-4xl tracking-tight text-espresso mt-4">Your ledger at a glance</h2>
            </div>
            <p className="text-sm text-espresso-soft max-w-xs">A sample of the record view. Each row reads its own status in your timezone.</p>
          </div>
          <div className="mt-8 bg-white border border-warmborder rounded-[1.5rem] overflow-hidden shadow-[0_30px_70px_-50px_rgba(42,36,33,0.5)]">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-warmborder bg-cream/60 text-left text-xs uppercase tracking-wider text-espresso-soft">
                  <th className="px-5 py-4 font-semibold">Reference</th>
                  <th className="px-5 py-4 font-semibold">Source / Client</th>
                  <th className="px-5 py-4 font-semibold text-right">Amount</th>
                  <th className="px-5 py-4 font-semibold">Expected / Due</th>
                  <th className="px-5 py-4 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {DEMO_ROWS.map((r) => (
                  <tr key={r.reference_code} className="border-b border-warmborder/60 last:border-0 hover:bg-oatmeal/50 transition-colors">
                    <td className="px-5 py-4 font-num font-medium text-espresso">{r.reference_code}</td>
                    <td className="px-5 py-4 text-espresso-soft">{r.name}</td>
                    <td className="px-5 py-4 text-right font-num font-semibold text-espresso">{r.amount}</td>
                    <td className="px-5 py-4 text-espresso-soft">{r.date}</td>
                    <td className="px-5 py-4"><StatusBadge status={r.display_status} daysOverdue={r.days_overdue} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* SMS visualizer */}
      <section id="alerts" className="max-w-6xl mx-auto px-6 py-20 lg:py-28">
        <div className="text-center max-w-2xl mx-auto">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold text-[#8C6D58]"><MessageSquare size={14} /> Text alerts</span>
          <h2 className="font-serif font-normal text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-4 text-espresso leading-tight">The only texts we'd ever send.</h2>
          <p className="body text-base sm:text-lg text-espresso-soft mt-4 leading-relaxed">
            Consent is never pre-checked and never bundled with anything else. Every message is transactional —
            about your account, never marketing. Tap one to preview it.
          </p>
        </div>

        {/* Mobile chips */}
        <div className="lg:hidden flex flex-wrap justify-center gap-2 mt-8 max-w-2xl mx-auto" data-testid="sms-tabs">
          {SMS_TABS.map((t) => {
            const Icon = TAB_ICONS[t.key] || MessageSquare;
            const active = tab.key === t.key;
            return (
              <button key={t.key} onClick={() => setTab(t)} data-testid={`sms-tab-${t.key}-m`}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${active ? "bg-espresso text-cream border-espresso" : "bg-white text-espresso-soft border-warmborder hover:bg-oatmeal"}`}>
                <Icon size={13} /> {t.label}
              </button>
            );
          })}
        </div>

        {/* Desktop flanking chips */}
        <div className="hidden lg:grid grid-cols-[1fr_auto_1fr] gap-10 items-center mt-14">
          <div className="flex flex-col items-end gap-3">
            {SMS_TABS.slice(0, 5).map((t) => {
              const Icon = TAB_ICONS[t.key] || MessageSquare;
              const active = tab.key === t.key;
              return (
                <button key={t.key} onClick={() => setTab(t)} data-testid={`sms-tab-${t.key}`}
                  className={`group inline-flex items-center gap-2 pl-4 pr-3 py-2 rounded-full text-sm font-semibold border transition-all duration-200 ${active ? "bg-espresso text-cream border-espresso shadow-md translate-x-1" : "bg-white text-espresso-soft border-warmborder hover:bg-oatmeal hover:translate-x-1"}`}>
                  <Icon size={15} className={active ? "text-lime" : "text-olive"} /> {t.label}
                  <ChevronRight size={15} className={`transition-opacity ${active ? "opacity-100" : "opacity-0 group-hover:opacity-40"}`} />
                </button>
              );
            })}
          </div>

          <PhoneDemo body={tab.body} messageKey={tab.key} />

          <div className="flex flex-col items-start gap-3">
            {SMS_TABS.slice(5).map((t) => {
              const Icon = TAB_ICONS[t.key] || MessageSquare;
              const active = tab.key === t.key;
              return (
                <button key={t.key} onClick={() => setTab(t)} data-testid={`sms-tab-${t.key}`}
                  className={`group inline-flex items-center gap-2 pr-4 pl-3 py-2 rounded-full text-sm font-semibold border transition-all duration-200 ${active ? "bg-espresso text-cream border-espresso shadow-md -translate-x-1" : "bg-white text-espresso-soft border-warmborder hover:bg-oatmeal hover:-translate-x-1"}`}>
                  <ChevronLeft size={15} className={`transition-opacity ${active ? "opacity-100" : "opacity-0 group-hover:opacity-40"}`} />
                  <Icon size={15} className={active ? "text-lime" : "text-olive"} /> {t.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="lg:hidden mt-10 flex justify-center">
          <PhoneDemo body={tab.body} messageKey={tab.key} />
        </div>
      </section>

      {/* Act III — Carrier-compliant opt-in */}
      <section className="bg-olive-light border-y border-warmborder">
        <div className="max-w-4xl mx-auto px-6 py-20 lg:py-28 text-center">
          <span className="text-xs uppercase tracking-[0.22em] font-semibold text-olive">Turn it on, on your terms</span>
          <h2 className="font-serif font-normal text-3xl sm:text-4xl tracking-tight text-espresso mt-4 leading-tight">Turn it on. Full transparency.</h2>
          <p className="body text-base text-espresso-soft mt-4 max-w-xl mx-auto leading-relaxed">
            Alerts are off until you decide otherwise. Enter your number, tap once, and you're in control —
            reply STOP any time and everything stops.
          </p>
          <div className="mt-10">
            <SmsOptInCard />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-6xl mx-auto px-6 py-20 lg:py-24">
        <div className="bg-espresso rounded-[2rem] p-10 sm:p-16 text-center relative overflow-hidden">
          <div className="absolute -top-24 -right-16 w-80 h-80 rounded-full bg-olive/25 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-20 -left-12 w-64 h-64 rounded-full bg-olive/25 blur-3xl pointer-events-none" />
          <h2 className="relative font-serif font-light text-3xl sm:text-4xl lg:text-5xl tracking-tight text-cream leading-tight">Start tracking what you're owed.</h2>
          <p className="relative text-cream/70 mt-4 max-w-md mx-auto leading-relaxed">Set up your ledger in under a minute. No card, no bookkeeping, no fuss.</p>
          <Link to="/signup"><Button size="lg" className="relative mt-8 rounded-full px-8 h-12 bg-lime text-espresso hover:bg-lime-dark hover:text-espresso" data-testid="cta-start">Start tracking free <ArrowRight size={18} className="ml-1.5" /></Button></Link>
        </div>
      </section>

      {/* Footer — deep warm charcoal */}
      <SiteFooter />
    </div>
  );
}
